import styled, { css } from 'styled-components'
import { Icon, IconRegistry } from '../icon'
import { Badge } from '../badge'
import { NavItemBadge } from './navigation.types'

// Matches the label's height (line-height plus its 1px underline border) so an
// icon-only collapsed row is as tall as a labelled one and rows don't shift on expand.
const IconLabelWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    min-height: calc(${theme.sizes.m} + 1px);
  `}
`

const IconContainer = styled.div<{ $isCollapsed: boolean }>`
  width: ${({ theme, $isCollapsed }) =>
    $isCollapsed ? 'auto' : theme.sizes.l};
  display: flex;
  align-items: center;
  transition: all ${({ theme }) => theme.transitionTime} ease;
`

const LinkLabel = styled.span`
  ${({ theme }) => css`
    transition: all ${theme.transitionTime} ease;
    border-bottom: 1px solid transparent;
  `}
`

const ChevronContainer = styled.div<{ $isOpen: boolean }>`
  ${({ theme, $isOpen }) => css`
    display: flex;
    align-items: center;
    transition: all ${theme.transitionTime} ease;
    opacity: 1;
    margin-left: ${theme.sizes.xs};
    transform: rotate(${$isOpen ? '180deg' : '0deg'});
  `}
`

const BadgeContainer = styled.div`
  ${({ theme }) => css`
    margin-left: ${theme.sizes.xs};
    display: flex;
    align-items: center;
    min-width: 28px;
  `}
`

const StyledMainLink = styled.div<{ $isActive: boolean; $isSubItem: boolean }>`
  ${({ theme, $isActive, $isSubItem }) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.sizes.xs};
    font-size: 0.875rem;
    line-height: ${theme.sizes.m};
    text-decoration: none;
    transition: all ${theme.transitionTime} ease;
    cursor: pointer;
    color: ${theme.colors.gray700};

    ${IconContainer} path {
      fill: ${theme.colors.gray700};
    }

    &:link,
    &:visited {
      color: ${theme.colors.gray700};
    }

    &:hover,
    &:active {
      color: ${theme.colors.black};

      ${IconContainer} path {
        fill: ${theme.colors.primary400};
      }

      ${LinkLabel} {
        border-color: ${theme.colors.primary400};
      }

      ${ChevronContainer} {
        opacity: 1;
      }
    }

    &:active {
      color: ${theme.colors.black};

      ${LinkLabel} {
        border-color: transparent;
      }
    }

    ${$isActive &&
    css`
      color: ${theme.colors[$isSubItem ? 'primary400' : 'black']};

      ${IconContainer} path {
        fill: ${theme.colors.primary400};
      }

      ${LinkLabel} {
        border-color: transparent;
      }
    `}
  `}
`

interface NavItemProps {
  label: string
  isActive: boolean
  isExpanded?: boolean
  icon?: keyof typeof IconRegistry
  onClick?: VoidFunction
  hasChevron?: boolean
  isSubItem?: boolean
  badge?: NavItemBadge
  isCollapsed?: boolean
}

export const NavItem = ({
  label,
  isActive,
  isExpanded = false,
  icon,
  onClick,
  hasChevron,
  isSubItem = false,
  badge,
  isCollapsed = false,
}: NavItemProps) => {
  return (
    <StyledMainLink
      $isActive={isActive}
      $isSubItem={isSubItem}
      onClick={onClick}
    >
      <IconLabelWrapper>
        {icon ? (
          <IconContainer $isCollapsed={isCollapsed}>
            <Icon name={icon} size={5} />
          </IconContainer>
        ) : null}
        {isCollapsed ? null : <LinkLabel>{label}</LinkLabel>}
        {badge && !isCollapsed && (
          <BadgeContainer>
            <Badge variant={badge.variant || 'default'} icon={badge.icon}>
              {badge.text}
            </Badge>
          </BadgeContainer>
        )}
      </IconLabelWrapper>
      {hasChevron && !isCollapsed ? (
        <ChevronContainer $isOpen={isExpanded}>
          <Icon name={'chevron-down'} size="s" color="gray400" />
        </ChevronContainer>
      ) : null}
    </StyledMainLink>
  )
}
